<script>
import { h } from "vue";
import SecondaryPageLayout from "@/Layouts/SecondaryPageLayout.vue";
import AboutHero from "@/Components/Brand/AboutHero.vue";

export default {
    layout: (_, page) =>
        h(
            SecondaryPageLayout,
            { title: "About Gift Hero" },
            { default: () => page, hero: () => h(AboutHero) },
        ),
};
</script>

<script setup>
import { Head } from "@inertiajs/vue3";
import FaqAccordion from "@/Components/Brand/FaqAccordion.vue";
import TeamMemberCard from "@/Components/Brand/TeamMemberCard.vue";

defineProps({
    team: {
        type: Array,
        required: true,
    },
});

// Figma supplies only the first answer; the rest reuse the copy from the
// previous About page and the affiliate disclosure below, reviewed by
// product in GH-1158.
const faqs = [
    {
        question: "What is Gift Hero?",
        answer: "Sure, you’ve used wedding and baby registries. But what about all of life’s other gift-giving occasions? A Gift Hero wish list is like a soft sell for birthdays, Mother’s Day, and even Christmas—all in one place. Your friends and family will thank&nbsp;you.",
    },
    {
        question: "What are the benefits of a universal wish list?",
        answer: "You’ve made your lists and checked them twice...and again...and again. Registering with multiple retailers isn’t just a pain for you to maintain but also a nuisance for your loved ones to decipher. With Gift Hero, anytime you see something online that you need or want, you can add that item to a wish list using your desktop, smartphone, or&nbsp;tablet.",
    },
    {
        question: "Is Gift Hero part of any affiliate programs?",
        answer: "Yes. Gift Hero participates in the Amazon Services LLC Associates Program and several other affiliate networks, which means we may earn a small commission when you buy through links on the site—at no extra cost to you. See the affiliate disclosure below for the full&nbsp;list.",
    },
    {
        question: "How do I add to my wish list?",
        answer: `Anytime you see something you want online, add it to a wish list with the <a href="${route("browser-extensions")}">Gift Hero browser extension</a>, the free <a href="${route("mobile")}">Add to Wish List app</a> for iOS and Android, or by pasting the product link into your list on&nbsp;gifthero.com.`,
    },
];
</script>

<template>
    <Head>
        <title>About Gift Hero</title>
        <meta name="fo-verify" content="ace4acac-728c-4f7a-b9c0-2bd0d3327c2c" />
        <meta
            name="description"
            content="Learn more about Gift Hero, a universal wish list management system."
        />
        <meta property="og:title" content="About Gift Hero" />
        <meta property="og:description" content="Learn more about Gift Hero." />
        <meta property="og:url" :content="route('about-us')" />
        <link rel="canonical" :href="route('about-us')" />
    </Head>

    <h2
        class="font-brand-display text-[36px] font-black italic uppercase leading-[1.05] text-black md:text-[42px]"
    >
        Frequently Asked Questions
    </h2>
    <FaqAccordion :items="faqs" class="mt-[23px]" />

    <!--
        The Team panel and the disclosure run 1065px wide in Figma, wider than
        the layout's 943px column, so from xl they break out symmetrically.
    -->
    <section class="mt-[94px] xl:-mx-[61px]">
        <div
            class="rounded-b-[8px] bg-[#ece6e4] px-5 pb-12 pt-10 md:px-10 md:pt-[45px]"
        >
            <h2
                class="text-center font-brand-display text-[48px] font-black italic uppercase leading-[0.87] tracking-[-0.02em] text-[#102a3d] md:text-[68px]"
            >
                The Team
            </h2>
            <div
                class="mt-10 grid gap-x-[30px] gap-y-8 md:mt-[47px] md:grid-cols-2"
            >
                <TeamMemberCard
                    v-for="member in team"
                    :key="`${member.firstName}-${member.lastName}`"
                    :member="member"
                />
            </div>
        </div>

        <div class="mt-11">
            <h3
                class="mb-4 text-[23px] font-semibold leading-[1.3] text-brand-navy"
            >
                Affiliate Disclosure
            </h3>
            <p class="m-0 text-[15px] leading-[19px] text-brand-navy">
                Gift Hero is a participant in the Amazon Services LLC Associates
                Program, an affiliate advertising program designed to provide a
                means for sites to earn advertising fees by advertising and
                linking to Amazon.com.
            </p>
            <p class="m-0 text-[15px] leading-[19px] text-brand-navy">
                Gift Hero also participates in the SkimLinks, CJ, LinkShare,
                Impact Radius, ShareASale and Pepperjam Network affiliate
                programs.
            </p>
        </div>
    </section>
</template>
